// =============================================================================
// insightsExcel.js
// Export one AI Insights answer as .xlsx.
//
// The sixth writer in this folder and deliberately the shape of the other five —
// ExcelJS, a workbook built in the browser from data already on screen, one
// triggerDownload. No server round trip, no AI involved: the figures are already
// here, and asking a model to re-emit them would be a second chance to get them
// wrong.
//
// EXPORT ONLY. The others round-trip because somebody edits the sheet and loads
// it back; there is nothing to load back here. An answer is a reading of the
// budget at a moment, not a source of it.
//
// -----------------------------------------------------------------------------
// TWO SHEETS, AND THE SPLIT IS THE POINT
// -----------------------------------------------------------------------------
// "Result"  the grid and nothing else. Headers on row 1, data from row 2, so it
//           sorts, filters and pivots like any other extract. A provenance block
//           above the headers would break every one of those.
//
// "About"   what the figures are, what was asked, what ran, which budget
//           versions they span, and whether Mantle or the model chose the scope.
//
// The moment a table leaves the screen it loses everything the screen was saying
// around it — which version, whose scope, first-100-rows-only. A spreadsheet of
// budget figures with no provenance is the exact artefact that gets mailed on and
// quoted six weeks later. So the provenance travels inside the file, and the one
// warning that can silently change a total — a truncated result — is repeated on
// the Result sheet itself, directly under the last row, where somebody about to
// sum a column will actually meet it.
// =============================================================================
import ExcelJS from 'exceljs';

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeName = (s) => String(s || 'answer').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 40);

// ---------------------------------------------------------------------------
// IS THIS COLUMN NUMBERS, OR IS IT CODES THAT LOOK LIKE NUMBERS?
//
// Decided per COLUMN, not per cell, and that is the whole difficulty. Postgres
// returns numeric as a string, so "1234.50" is a figure and must land in Excel
// as a number or nobody can sum it. But "00123" is a stock code and must land as
// text or Excel eats the leading zeros and silently corrupts it.
//
// Per-cell guessing gets both wrong: a strict round-trip test rejects "1234.50"
// (it re-renders as "1234.5"), and a loose one accepts "00123". Judging the
// whole column fixes it — a column of money is all money, and one leading zero
// anywhere is enough to prove the column is a code.
// ---------------------------------------------------------------------------
const NUMERIC = /^-?\d+(\.\d+)?$/;
const LEADING_ZERO = /^-?0\d/;

const isNumericColumn = (values) => {
  let seen = 0;
  for (const v of values) {
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'number') { seen += 1; continue; }
    if (typeof v !== 'string') return false;
    const t = v.trim();
    if (!NUMERIC.test(t) || LEADING_ZERO.test(t)) return false;
    seen += 1;
  }
  return seen > 0;
};

// Two decimals only where the data actually has them — a column of whole units
// shown as 1,234.00 reads as false precision.
const numberFormat = (values) =>
  values.some((v) => v !== null && v !== undefined && String(v).includes('.'))
    ? '#,##0.00'
    : '#,##0';

const SLATE = 'FF475569';
const AMBER_BG = 'FFFEF3C7';
const AMBER_TEXT = 'FF92400E';

// ---------------------------------------------------------------------------
// What this answer actually covers, in the words the UI uses for it.
//
// The two query paths give genuinely different guarantees and the file has to
// say which one it was. On the plan path Mantle applied the version, site and GL
// scope itself. On the direct-SQL path the model wrote the statement and Mantle
// applied none of them — so the honest line there is what the returned rows were
// found to span, or an admission that it could not be determined.
// ---------------------------------------------------------------------------
const coverageLines = (msg, versionId) => {
  const sql = msg.query?.mode === 'sql';
  const span = msg.query?.version_span;
  const out = [];

  if (sql) {
    out.push(['Query path', 'Direct SQL — the model wrote the statement']);
    out.push(['Scope applied by Mantle',
      'None. Mantle did not add the budget version, site or GL filters it adds on the standard path.']);
    if (span?.known) {
      out.push(['Budget versions in these figures', span.versions.join(', ')
        + (span.versions.length > 1 ? '  (more than one — do not total across them without checking)' : '')]);
    } else {
      out.push(['Budget versions in these figures',
        'UNKNOWN — the query returned no version column, so this cannot be confirmed.']);
    }
  } else {
    out.push(['Query path', 'Standard — the model chose the query, Mantle wrote and scoped the SQL']);
    out.push(['Scope applied by Mantle',
      'Budget version, site and the role’s GL and cost centre scope were applied by Mantle, not by the model.']);
    out.push(['Budget version', versionId || 'not recorded']);
  }

  out.push(['Figures sent to the AI service', msg.results_sent ? 'Yes' : 'No — the figures never left Mantle']);
  return out;
};

// ---------------------------------------------------------------------------
// buildWorkbook
//
// Separated from the download for one reason: it can then be tested. The part
// that can be wrong in a way nobody notices is the typing of the cells — a stock
// code silently reduced to a number, a column of money landing as text that will
// not sum — and that is provable in a test, where the download is not.
//
// `msg` is one assistant turn exactly as Thread renders it; `question` is the
// user turn it answered. Nothing is re-fetched: what is exported is what was on
// screen, which is the only arrangement in which the file and the screen cannot
// disagree.
// ---------------------------------------------------------------------------
export function buildWorkbook({ question, msg, versionId, modelLabel }) {
  const columns = msg?.columns || [];
  const rows = msg?.rows || [];
  if (!columns.length) throw new Error('That answer has no table to export.');

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  // ---- Sheet 1: the grid, and only the grid ---------------------------------
  const ws = wb.addWorksheet('Result', { views: [{ state: 'frozen', ySplit: 1 }] });

  const colValues = columns.map((_, i) => rows.map((r) => r[i]));
  const numeric = colValues.map(isNumericColumn);
  const formats = colValues.map(numberFormat);

  ws.addRow(columns);
  const head = ws.getRow(1);
  head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SLATE } };
  head.alignment = { vertical: 'middle' };
  head.height = 20;

  for (const r of rows) {
    // NULL STAYS EMPTY, NOT ZERO. The whole catalogue is built around the
    // difference between "no value" and "zero" — an uncosted product is unknown,
    // not free — and writing 0 here would throw that away at the last step.
    ws.addRow(columns.map((_, i) => {
      const v = r[i];
      if (v === null || v === undefined || v === '') return null;
      return numeric[i] ? Number(v) : v;
    }));
  }

  columns.forEach((name, i) => {
    const col = ws.getColumn(i + 1);
    if (numeric[i]) col.numFmt = formats[i];
    const widest = Math.max(
      String(name).length,
      ...colValues[i].slice(0, 500).map((v) => (v === null || v === undefined ? 0 : String(v).length))
    );
    col.width = Math.min(Math.max(widest + 2, 10), 44);
  });

  if (rows.length) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
  }

  // THE ONE WARNING THAT CHANGES A TOTAL, put where a total gets made. Everything
  // else about provenance lives on the About sheet; this cannot, because the
  // person summing column D is looking at column D.
  if (msg.truncated) {
    ws.addRow([]);
    const warn = ws.addRow([
      `FIRST ${msg.row_count} ROWS ONLY — the query returned more than could be shown. `
      + 'Any total of this sheet covers just these rows.',
    ]);
    warn.font = { bold: true, color: { argb: AMBER_TEXT } };
    warn.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: AMBER_BG } };
    ws.mergeCells(warn.number, 1, warn.number, Math.max(columns.length, 1));
  }

  // ---- Sheet 2: where these numbers came from -------------------------------
  const about = wb.addWorksheet('About');
  about.getColumn(1).width = 34;
  about.getColumn(2).width = 110;

  const section = (title) => {
    about.addRow([]);
    const r = about.addRow([title]);
    r.font = { bold: true, size: 11, color: { argb: SLATE } };
  };
  const pair = (k, v) => {
    const r = about.addRow([k, v == null ? '' : String(v)]);
    r.getCell(1).font = { bold: true };
    r.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    return r;
  };

  const title = about.addRow(['Mantle — AI Insights answer']);
  title.font = { bold: true, size: 14 };

  section('The question');
  pair('Asked', question || '(not recorded)');
  if (msg.intent) pair('What the query answers', msg.intent);

  section('What these figures cover');
  for (const [k, v] of coverageLines(msg, versionId)) pair(k, v);
  pair('Rows in this file', rows.length);
  if (msg.truncated) {
    pair('Truncated', `Yes — first ${msg.row_count} rows only. Any total covers just these rows.`)
      .getCell(2).font = { bold: true, color: { argb: AMBER_TEXT } };
  }

  if (msg.query?.mode === 'sql' && msg.steps) {
    const sqlStep = msg.steps.find((s) => s.sql);
    if (sqlStep) {
      section('The SQL that ran');
      const r = about.addRow(['SQL', sqlStep.sql]);
      r.getCell(1).font = { bold: true };
      r.getCell(2).alignment = { wrapText: true, vertical: 'top' };
      r.height = 90;
    }
  }

  if (msg.content) {
    section('The model’s reading of the figures');
    // LABELLED AS COMMENTARY, not as a finding. On screen the prose sits below
    // the table it describes and the ordering makes the relationship obvious; in
    // a file it is just another paragraph, so it says what it is.
    const r = about.addRow(['Written by the AI model', String(msg.content)]);
    r.getCell(1).font = { bold: true };
    r.getCell(2).alignment = { wrapText: true, vertical: 'top' };
    r.height = Math.min(240, 16 + String(msg.content).length / 4);
  }

  section('Provenance');
  pair('Model', modelLabel || '(not recorded)');
  pair('Exported', new Date().toLocaleString());
  pair('Source', 'Mantle — AI Insights. These are budget figures, not audited actuals.');

  return wb;
}

// What Thread calls. Build, serialise, hand to the browser.
export async function exportAnswer({ question, msg, versionId, modelLabel }) {
  const wb = buildWorkbook({ question, msg, versionId, modelLabel });
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `Mantle_Insights_${safeName(msg.intent || question)}.xlsx`);
}

export { isNumericColumn };
export default { exportAnswer, buildWorkbook };
